import type { Zone } from './zones';

/**
 * Keeper sprite asset registry. Per spec §2.2, dive frames are SEPARATE
 * Phaser textures (not atlas frames) — KeeperSprite switches between them
 * via `setTexture(key)`, NOT `setFrame(name)`. Each in-goal Zone maps to
 * one dive texture; `'off'` is deliberately absent — when the player misses
 * the goal, the keeper stays on the ready texture.
 *
 * Base source size is 16x24 grayscale (1:1.5 aspect, NES Tecmo standard).
 * Originally specced as 4x26 to mirror the legacy keeperRect's stretched
 * bounding box; T30 smoke gate caught that this produced a stick-like
 * silhouette. Rescaled to 16x24 with uniform `setScale` in PlayScene so the
 * figure renders with realistic keeper proportions across viewports.
 * See spec §4.5 and Plan #9 §13 catches.
 */
export const KEEPER_READY_TEXTURE = 'keeper-ready';

export const DIVE_TEXTURE_BY_ZONE: Record<Zone, string> = {
  LF: 'keeper-dive-LF',
  LA: 'keeper-dive-LA',
  CF: 'keeper-dive-CF',
  CA: 'keeper-dive-CA',
  RF: 'keeper-dive-RF',
  RA: 'keeper-dive-RA',
};

export const KEEPER_TEXTURE_KEYS: readonly string[] = [
  KEEPER_READY_TEXTURE,
  DIVE_TEXTURE_BY_ZONE.LF,
  DIVE_TEXTURE_BY_ZONE.LA,
  DIVE_TEXTURE_BY_ZONE.CF,
  DIVE_TEXTURE_BY_ZONE.CA,
  DIVE_TEXTURE_BY_ZONE.RF,
  DIVE_TEXTURE_BY_ZONE.RA,
] as const;

export const BASE_KEEPER_SOURCE = { width: 16, height: 24 } as const;

/**
 * KeeperSprite pose: either the idle 'ready' state, or one of the six
 * in-goal Zones the keeper has dived to. `'off'` is excluded — keeper
 * stays on 'ready' when the player shoots wide of the goal.
 */
export type KeeperPose = 'ready' | Exclude<Zone, 'off'>;
